import React, { useEffect, useRef, useState } from 'react';
import {
  Environment,
  RecoveryPoint,
  RestoreRun,
  deleteRecoveryPoint,
  pinRecoveryPoint,
  unpinRecoveryPoint,
  testRestoreRecoveryPoint,
} from '../api';
import { CreateBackupModal } from './CreateBackupModal';
import { RestoreModal } from './RestoreModal';
import { BackupPolicyCard } from './BackupPolicyCard';
import { ConfirmDialog, LoadingRows, StatusBadge } from './ui';
import { ActionMenu, Callout, Icon, Section } from './primitives';
import { ResponsiveTable } from './primitives/ResponsiveTable';
import type { Column } from './primitives/ResponsiveTable';
import type { MenuAction } from './primitives';
import { labelFor, recoveryPointTypeLabel } from '../labels';
import { formatBytes, relativeTime } from './project/projectHealth';
import { actionLabel } from './operation/actionLabels';
import { backUpAgainLabel } from './project/BackupPosition';

const NO_SERVER = '00000000-0000-0000-0000-000000000000';

/** When a request fails and the server gives no message. */
const noReason = 'The server gave no reason.';

/**
 * copiesOf says where a recovery point's copies are, from what the control
 * plane recorded for each artifact (RES-039): a backup only on its server
 * is lost with the server; one stored off-host restores onto a
 * replacement, and its copy is read back and decrypted periodically.
 */
export function copiesOf(rp: RecoveryPoint): { value: 'both' | 'off_host_only' | 'local_only' | 'lost' | 'pending'; detail?: string; title?: string } {
  const meta = rp.metadata_json ?? {};
  if (meta.lost_reason) return { value: 'lost', detail: 'Cannot be restored', title: String(meta.lost_reason) };
  if (rp.status !== 'ready') return { value: 'pending' };
  const usable = (rp.artifacts ?? []).filter((a) => a.status !== 'failed');
  const offHost = usable.length > 0 ? usable.every((a) => Boolean(a.uploaded_at)) : meta.off_host === true;
  if (!offHost) {
    const error = usable.find((a) => a.last_upload_error)?.last_upload_error ?? meta.upload_error;
    return { value: 'local_only', detail: 'Uploaded once storage is reachable', title: error ? `Last upload attempt: ${error}` : undefined };
  }
  const readBack = usable.map((a) => a.remote_verified_at).filter(Boolean) as string[];
  const allRead = usable.length > 0 && readBack.length === usable.length;
  const oldest = allRead ? readBack.sort()[0] : undefined;
  return {
    value: rp.server_id === NO_SERVER ? 'off_host_only' : 'both',
    detail: oldest ? `Read back ${relativeTime(oldest)}` : 'Not read back yet',
    title: oldest ? 'The off-host copy was downloaded, decrypted and matched its checksum.' : 'The off-host copy is read back and checked within a day of the upload, then weekly.',
  };
}

/**
 * restoreTestRunOf is the run of a backup's latest restore test, kept by the
 * server in the backup's metadata (CO-169): the log can be opened while the
 * test runs and after it ended.
 */
export function restoreTestRunOf(rp: RecoveryPoint): string | undefined {
  const id = rp.metadata_json?.restore_test_execution_id;
  return typeof id === 'string' && id !== '' ? id : undefined;
}

/** contentsOf names what a recovery point holds. */
export function contentsOf(rp: RecoveryPoint): string {
  const kinds = (rp.artifacts ?? []).filter((a) => a.status !== 'failed').map((a) => a.artifact_type);
  if (rp.metadata_json?.media_failed) return 'Database only (media capture failed)';
  if (kinds.includes('database_dump') && kinds.includes('media_archive')) return 'Database and media';
  if (kinds.includes('media_archive')) return 'Media';
  return 'Database';
}

interface RecoveryPointsViewProps {
  projectId: string;
  /** Recovery points and new backups belong to this environment. */
  environment: Environment;
  recoveryPoints: RecoveryPoint[];
  loading: boolean;
  /** A restore that left the live data partially replaced (RES-041). */
  incompleteRestore?: RestoreRun | null;
  currentUserEmail?: string;
  /** Names the project in the restore confirmation. */
  projectName?: string;
  onRefresh: () => void;
  onNavigateToExecution?: (executionId: string) => void;
  /**
   * One-shot intent from an incident's suggested action (UX-01): open the
   * real "Back up now" dialog, or bring one backup's "Test restore" into
   * view with focus on it. Handled once, then reported back.
   */
  intent?: RecoveryIntent;
  onIntentHandled?: () => void;
}

export type RecoveryIntent = { kind: 'backup' } | { kind: 'test_restore'; recoveryPointId: string };

export const RecoveryPointsView: React.FC<RecoveryPointsViewProps> = ({
  projectId,
  environment,
  recoveryPoints,
  loading,
  incompleteRestore,
  currentUserEmail,
  projectName,
  onRefresh,
  onNavigateToExecution,
  intent,
  onIntentHandled,
}) => {
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const testRestoreButtons = useRef(new Map<string, HTMLButtonElement>());
  const intentDone = useRef(false);
  const [focusedPointId, setFocusedPointId] = useState<string | null>(null);
  const [intentNotice, setIntentNotice] = useState<string | null>(null);

  useEffect(() => {
    // A new intent (Back up again from the status above) is handled again once the last one is cleared.
    if (!intent) {
      intentDone.current = false;
      return;
    }
    if (intentDone.current) return;
    if (intent.kind === 'backup') {
      intentDone.current = true;
      setShowCreateModal(true);
      onIntentHandled?.();
      return;
    }
    // The list may still be arriving: focus the backup once it is there,
    // and only say it is gone once the list has loaded without it.
    const point = recoveryPoints.find((rp) => rp.id === intent.recoveryPointId);
    const button = testRestoreButtons.current.get(intent.recoveryPointId);
    if (point && point.status === 'ready' && button) {
      intentDone.current = true;
      setIntentNotice(null);
      setFocusedPointId(point.id);
      button.scrollIntoView({ block: 'center' });
      button.focus();
      onIntentHandled?.();
    } else if (!loading && recoveryPoints.length > 0) {
      setIntentNotice('That backup can no longer be restore-tested: it was removed or is not ready. Pick another backup below.');
    }
  }, [intent, loading, recoveryPoints, onIntentHandled]);
  const [selectedRestoreRp, setSelectedRestoreRp] = useState<RecoveryPoint | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  // What was being done and what the server said, so the message names both.
  const [actionError, setActionError] = useState<{ title: string; message: string } | null>(null);
  const [showPolicy, setShowPolicy] = useState<boolean>(false);
  const [rpToDelete, setRpToDelete] = useState<RecoveryPoint | null>(null);


  const handleTogglePin = async (rp: RecoveryPoint) => {
    setActionLoadingId(rp.id);
    setActionError(null);
    try {
      if (rp.pinned) {
        await unpinRecoveryPoint(rp.id);
      } else {
        await pinRecoveryPoint(rp.id);
      }
      onRefresh();
    } catch (err: any) {
      setActionError({ title: rp.pinned ? 'The backup was not unpinned' : 'The backup was not pinned', message: err.message || noReason });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async () => {
    const rp = rpToDelete;
    if (!rp) return;
    setActionLoadingId(rp.id);
    setActionError(null);
    try {
      await deleteRecoveryPoint(rp.id);
      setRpToDelete(null);
      onRefresh();
    } catch (err: any) {
      setActionError({ title: 'The backup was not deleted', message: err.message || noReason });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleTestRestore = async (rp: RecoveryPoint) => {
    setActionLoadingId(rp.id);
    setActionError(null);
    try {
      const started = await testRestoreRecoveryPoint(rp.id);
      onRefresh();
      // Like a backup and a restore, a test opens its run: the steps and the
      // log are the answer to "is anything happening?".
      if (started.execution?.id && onNavigateToExecution) onNavigateToExecution(started.execution.id);
    } catch (err: any) {
      setActionError({ title: 'The restore test did not start', message: err.message || noReason });
    } finally {
      setActionLoadingId(null);
    }
  };

  const takenLabel = (rp: RecoveryPoint) => new Date(rp.created_at).toLocaleString();

  // Provenance, copies and the checksum open under a row (CO-161).
  const [openPoints, setOpenPoints] = useState<Set<string>>(new Set());
  const togglePoint = (id: string) =>
    setOpenPoints((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const columns: Column<RecoveryPoint>[] = [
    {
      id: 'taken',
      header: 'Taken',
      card: 'title',
      width: 16,
      cell: (rp) => {
        const open = openPoints.has(rp.id);
        return (
          <span className="cell-inline recovery-point__taken">
            <button
              type="button"
              className="component-cell__toggle"
              aria-expanded={open}
              aria-label={`${open ? 'Hide' : 'Show'} details of the backup from ${takenLabel(rp)}`}
              onClick={() => togglePoint(rp.id)}
              data-testid={`recovery-point-toggle-${rp.id}`}
            >
              <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
            </button>
            <span className="cell-stack">
              <span className="cell-stack__primary">{new Date(rp.created_at).toLocaleDateString()}</span>
              <span className="cell-stack__secondary">{new Date(rp.created_at).toLocaleTimeString()}</span>
            </span>
          </span>
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      width: 'fill',
      cell: (rp) => (
        <span className="cell-stack">
          <span className="cell-stack__primary">
            {labelFor(recoveryPointTypeLabel, rp.type)}
            {rp.pinned ? (
              <>
                {' '}
                <span className="badge" title="Pinned: kept when automatic retention removes older backups">
                  Pinned
                </span>
              </>
            ) : null}
          </span>
          {rp.status === 'ready' ? <span className="cell-stack__secondary">{contentsOf(rp)}</span> : null}
        </span>
      ),
    },
    {
      id: 'size',
      header: <span className="rt-num">Size</span>,
      cardLabel: 'Size',
      width: 10,
      cell: (rp) =>
        rp.status === 'ready' || rp.total_size_bytes > 0 ? (
          <span className="rt-num tabular-nums">{formatBytes(rp.total_size_bytes)}</span>
        ) : (
          <span className="rt-num muted">Nothing stored</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      card: 'status',
      // Wider than a usual status: "Only on the server" stays on one line.
      width: 14,
      cell: (rp) => {
        const copies = copiesOf(rp);
        const copyProblem = copies.value === 'local_only' || copies.value === 'lost';
        return (
          <span className="stack stack--tight">
            <StatusBadge kind="recoveryPoint" value={rp.status} />
            {/* A checksum only means something for a backup that exists; only a
                problem with it is shown here, the proof is in the details. */}
            {rp.status === 'ready' && rp.verification_status === 'verification_failed' ? <StatusBadge kind="verification" value={rp.verification_status} /> : null}
            {copyProblem ? <StatusBadge kind="backupCopies" value={copies.value} title={copies.title} /> : null}
            {/* A failed backup leads to its run: the failing step and its log (CO-100). */}
            {(rp.status === 'failed' || rp.status === 'verification_failed') && rp.execution_id && onNavigateToExecution ? (
              <button type="button" className="link-button" onClick={() => onNavigateToExecution(rp.execution_id!)} data-testid="backup-why-failed">
                {actionLabel.viewError}
              </button>
            ) : null}
          </span>
        );
      },
    },
    {
      id: 'restore',
      header: 'Restore test',
      // Wide enough for "Restore test failed" on one line, above its log link.
      width: 20,
      cell: (rp) => {
        const run = restoreTestRunOf(rp);
        // A backup that was not stored has nothing to test: "not tested" would
        // read as a gap in the testing rather than the absence of a backup.
        if (rp.status !== 'ready') return <span className="muted">No backup to test</span>;
        return (
          <span className="stack stack--tight">
            <StatusBadge kind="restoreTest" value={rp.restore_test_status} />
            {run && onNavigateToExecution && rp.restore_test_status !== 'not_tested' ? (
              <button type="button" className="link-button" onClick={() => onNavigateToExecution(run)} data-testid={`restore-test-log-${rp.id}`}>
                {rp.restore_test_status === 'testing' ? 'Watch the test' : actionLabel.fullLogs}
              </button>
            ) : null}
          </span>
        );
      },
    },
  ];

  const lastProvenBackup =
    rpToDelete !== null &&
    rpToDelete.restore_test_status === 'restore_verified' &&
    recoveryPoints.filter((rp) => rp.status === 'ready' && rp.restore_test_status === 'restore_verified').length === 1;

  return (
    <div className="recovery-points-section stack">
      <Section
        title="Backups"
        // The project header's Deploy is the page's primary action (audit B1);
        // an empty list shows its own next steps instead of repeating these.
        actions={
          recoveryPoints.length > 0 ? (
            <>
              <button type="button" className="button button--secondary" onClick={() => setShowPolicy(!showPolicy)} aria-expanded={showPolicy}>
                {showPolicy ? 'Hide schedule' : 'Schedule and retention'}
              </button>
              <button type="button" className="button button--secondary" onClick={() => setShowCreateModal(true)}>
                Back up now
              </button>
            </>
          ) : undefined
        }
      >
        {incompleteRestore ? (
          <IncompleteRestoreCallout
            run={incompleteRestore}
            environmentName={environment.name}
            checkpoint={recoveryPoints.find((rp) => rp.id === incompleteRestore.pre_restore_recovery_point_id && rp.status === 'ready') ?? null}
            onRestoreCheckpoint={setSelectedRestoreRp}
          />
        ) : null}
        {actionError ? (
          <Callout tone="danger" title={actionError.title}>
            {actionError.message}
          </Callout>
        ) : null}
        {intentNotice ? (
          <Callout tone="warning" role="status" testId="recovery-intent-notice">
            {intentNotice}
          </Callout>
        ) : null}

        {showPolicy && <BackupPolicyCard projectId={projectId} onPolicyUpdated={onRefresh} />}

        {loading && recoveryPoints.length === 0 ? (
          <LoadingRows label="Loading recovery points" rows={3} rowHeight="44px" />
        ) : recoveryPoints.length === 0 ? (
          <div className="empty-row" data-testid="no-backups">
            <p className="quiet-empty">No backups</p>
            <div className="empty-state__actions">
              <button type="button" className="button button--secondary" onClick={() => setShowCreateModal(true)}>
                Back up now
              </button>
              <button type="button" className="button button--ghost" onClick={() => setShowPolicy(true)}>
                Set a schedule
              </button>
            </div>
          </div>
        ) : (
          <ResponsiveTable
            rows={recoveryPoints}
            rowKey={(rp) => rp.id}
            caption={`Backups of ${environment.name}, newest first`}
            className="recovery-points-table"
            testId="recovery-points"
            columns={columns}
            actionsSize="lg"
            actions={(rp) => {
              const busy = actionLoadingId === rp.id;
              const more: MenuAction[] = [];
              if (rp.execution_id && onNavigateToExecution) more.push({ label: actionLabel.fullLogs, onClick: () => onNavigateToExecution(rp.execution_id!) });
              more.push({ label: rp.pinned ? 'Unpin' : 'Pin', onClick: () => handleTogglePin(rp), disabled: busy });
              more.push({ label: 'Delete backup', danger: true, onClick: () => setRpToDelete(rp), disabled: busy });
              return (
                <>
                  {rp.status === 'ready' ? (
                    <>
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) testRestoreButtons.current.set(rp.id, el);
                          else testRestoreButtons.current.delete(rp.id);
                        }}
                        className="button button--secondary button--small"
                        onClick={() => handleTestRestore(rp)}
                        disabled={busy}
                        title="Loads this backup into a throwaway database to prove it restores, then removes it. The live store is not touched."
                      >
                        Test restore
                      </button>
                      <button
                        type="button"
                        className="button button--danger-quiet button--small"
                        onClick={() => setSelectedRestoreRp(rp)}
                        disabled={busy}
                        title={`Replace the database of ${environment.name} with this backup`}
                      >
                        Restore
                      </button>
                    </>
                  ) : null}
                  {/* A failed backup leaves nothing to restore: the way forward is to take one again. */}
                  {rp.status === 'failed' || rp.status === 'verification_failed' ? (
                    <button type="button" className="button button--secondary button--small" onClick={() => setShowCreateModal(true)} disabled={busy}>
                      {backUpAgainLabel}
                    </button>
                  ) : null}
                  <ActionMenu size="sm" label={`More actions for the backup from ${takenLabel(rp)}`} items={more} />
                </>
              );
            }}
            detail={(rp) => (openPoints.has(rp.id) ? <RecoveryPointDetail rp={rp} onNavigateToExecution={onNavigateToExecution} /> : null)}
            rowProps={(rp) => ({ testId: `recovery-point-${rp.id}`, highlighted: focusedPointId === rp.id })}
          />
        )}
      </Section>

      <ConfirmDialog
        open={rpToDelete !== null}
        title="Delete this backup?"
        description={
          rpToDelete
            ? `The backup of ${environment.name} taken ${new Date(rpToDelete.created_at).toLocaleString()} (${formatBytes(rpToDelete.total_size_bytes)}) is removed from storage and can no longer be restored. This cannot be undone.${
                lastProvenBackup ? ` It is the only backup of ${environment.name} whose restore test passed: after this, no backup is proven to restore.` : ''
              }`
            : ''
        }
        // The last proven backup asks for the environment's name (UX-17).
        confirmText={lastProvenBackup ? environment.name : undefined}
        confirmLabel="Delete backup"
        danger
        busy={rpToDelete !== null && actionLoadingId === rpToDelete.id}
        onConfirm={handleDelete}
        onCancel={() => setRpToDelete(null)}
      />

      {showCreateModal && (
        <CreateBackupModal
          projectId={projectId}
          environment={environment}
          onClose={() => setShowCreateModal(false)}
          onCreated={(_rpId, execId) => {
            setShowCreateModal(false);
            onRefresh();
            if (onNavigateToExecution) {
              onNavigateToExecution(execId);
            }
          }}
        />
      )}

      {selectedRestoreRp && (
        <RestoreModal
          recoveryPoint={selectedRestoreRp}
          environmentName={environment.name}
          projectName={projectName}
          currentUserEmail={currentUserEmail}
          onClose={() => setSelectedRestoreRp(null)}
          onRestoring={(_restoreRunId, execId) => {
            setSelectedRestoreRp(null);
            onRefresh();
            if (onNavigateToExecution) {
              onNavigateToExecution(execId);
            }
          }}
        />
      )}
    </div>
  );
};

/** What a recovery point is made of and where it came from (CO-161). */
function RecoveryPointDetail({ rp, onNavigateToExecution }: { rp: RecoveryPoint; onNavigateToExecution?: (executionId: string) => void }) {
  return (
    <dl className="component-detail__facts recovery-point__detail" data-testid={`recovery-point-detail-${rp.id}`}>
      <div>
        <dt>Copies</dt>
        <dd>
          <CopiesCell rp={rp} />
        </dd>
      </div>
      <div>
        <dt>Checksum</dt>
        <dd>{rp.status === 'ready' ? <StatusBadge kind="verification" value={rp.verification_status} /> : <span className="muted">No backup to check</span>}</dd>
      </div>
      <div>
        <dt>Release</dt>
        <dd>{rp.commit_sha ? <code translate="no">{rp.commit_sha.slice(0, 7)}</code> : <span className="muted">Not recorded</span>}</dd>
      </div>
      <div>
        <dt>Contents</dt>
        <dd>{rp.status === 'ready' ? contentsOf(rp) : <span className="muted">Nothing stored</span>}</dd>
      </div>
      {rp.execution_id && onNavigateToExecution ? (
        <div>
          <dt>Run</dt>
          <dd>
            <button type="button" className="link-button" onClick={() => onNavigateToExecution(rp.execution_id!)}>
              {actionLabel.fullLogs}
            </button>
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

function CopiesCell({ rp }: { rp: RecoveryPoint }) {
  const copies = copiesOf(rp);
  return (
    <span className="stack stack--tight" data-testid="recovery-point-copies">
      <StatusBadge kind="backupCopies" value={copies.value} title={copies.title} />
      {copies.detail ? <span className="table__sub">{copies.detail}</span> : null}
    </span>
  );
}

/**
 * IncompleteRestoreCallout is the one message a half-finished restore must
 * not let anyone miss: what the live store holds now, that it was left in
 * maintenance, and the way back (the checkpoint the restore took first).
 */
function IncompleteRestoreCallout({
  run,
  environmentName,
  checkpoint,
  onRestoreCheckpoint,
}: {
  run: RestoreRun;
  environmentName: string;
  checkpoint: RecoveryPoint | null;
  onRestoreCheckpoint: (rp: RecoveryPoint) => void;
}) {
  const media = run.outcome === 'media_partially_extracted';
  const title = media
    ? `The last restore of ${environmentName} stopped part-way through the media`
    : `The last restore of ${environmentName} left the live database partially replaced`;
  const state = media
    ? 'The database was restored; some media files are from the backup and others are not.'
    : 'The import stopped part-way, so the database holds a mix of the data it had and the data from the backup.';
  // The server's reason ends with the error itself.
  const cause = run.failure_reason?.includes('Cause: ') ? run.failure_reason.slice(run.failure_reason.lastIndexOf('Cause: ') + 7) : run.failure_reason;
  const takenAt = checkpoint
    ? new Date(checkpoint.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <Callout
      tone="danger"
      title={title}
      testId="incomplete-restore"
      actions={
        checkpoint ? (
          <button type="button" className="button button--danger-quiet" onClick={() => onRestoreCheckpoint(checkpoint)}>
            Restore the checkpoint from {takenAt}
          </button>
        ) : undefined
      }
    >
      <p>
        {state} {run.maintenance_left_on ? 'The store was left in maintenance mode, so customers see the maintenance page. ' : ''}
        Deployments and other changes are refused until a restore of {environmentName} completes.
      </p>
      {checkpoint ? null : <p>Run the same restore again, or restore another backup.</p>}
      {cause ? (
        <p data-testid="incomplete-restore-cause">
          <strong>Reported cause:</strong> {cause}
        </p>
      ) : null}
    </Callout>
  );
}
